import { Card, InfoIcon, Stack, Tooltip } from '@ds/coolshop';
import { useProductTreeCtx } from '@modules/product';
import {
    ControlFieldElement,
    SelectFieldElement,
    TextFieldElement,
} from '@src/components/formFields';
import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

interface IMandatoryForSaveElemenProps {
    control: Control;
}

export enum MandatoryForSaveComparatorOptions {
    Equals = 'eq',
    NotEquals = 'ne',
}

export function MandatoryForSaveElement(props: IMandatoryForSaveElemenProps) {
    const { control } = props;
    const intl = useIntl();
    const { state } = useProductTreeCtx();

    const options = useMemo(
        () =>
            state.product.contentTypeGroups?.reduce<string[]>(
                (acc, ctGroup) => [
                    ...acc,
                    ...(ctGroup.contentTypes ?? []).map(ct => `${ctGroup.code}-${ct.code}`),
                ],
                [],
            ),
        [state.product],
    );

    return (
        <Card padding={2}>
            <Stack gap={1}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    paddingRight={1}
                >
                    <ControlFieldElement
                        control={control}
                        id="mandatoryForSave-isMandatory"
                        name="mandatoryForSave.isMandatory"
                        data-testid="mandatoryForSave-switch"
                        label={intl.formatMessage({
                            defaultMessage: 'Set field mandatory for save',
                            id: 'mandatory-for-save-field',
                        })}
                    />
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'mandatory-for-save-info',
                            defaultMessage:
                                'The current content type is considered mandatory for save, if the entered condition is met. If no condition is entered, only the switch value is considered.',
                        })}
                    >
                        <InfoIcon />
                    </Tooltip>
                </Stack>
                <Stack direction="row" gap={1}>
                    <SelectFieldElement
                        control={control}
                        id="mandatoryForSave-option"
                        name="mandatoryForSave.option"
                        labelId="Field"
                        data-testid="mandatoryForSave-option"
                        selectFieldTitle={{
                            inputLabelId: `Field`,
                            formatDefault: `Field`,
                            formatId: `Field`,
                            sxTitleTheme: () => ({ minWidth: 0, flex: 1 }),
                        }}
                        fieldOptions={{
                            keyId: 'id',
                            valueLabel: 'id',
                            dataCy: `mandatoryForSave-opt`,
                            nameLabel: `Field`,
                            options,
                            showEmptyOption: true,
                        }}
                        sxCustom={{ minWidth: 0 }}
                    />
                    <SelectFieldElement
                        control={control}
                        id="mandatoryForSave-comparator"
                        name="mandatoryForSave.comparator"
                        labelId="Comparator"
                        data-testid="mandatoryForSave-comparator"
                        selectFieldTitle={{
                            inputLabelId: `Comparator`,
                            formatDefault: `Comparator`,
                            formatId: `Comparator`,
                            sxTitleTheme: () => ({
                                minWidth: 0,
                                flex: 0.6,
                            }),
                        }}
                        fieldOptions={{
                            keyId: 'key',
                            valueLabel: 'value',
                            dataCy: `mandatoryForSave-comparator-opt`,
                            nameLabel: 'name',
                            options: [
                                { name: 'Equals', value: MandatoryForSaveComparatorOptions.Equals },
                                {
                                    name: 'Not Equals',
                                    value: MandatoryForSaveComparatorOptions.NotEquals,
                                },
                            ],
                            showEmptyOption: true,
                        }}
                        sxCustom={{ minWidth: 0 }}
                    />
                    <TextFieldElement
                        control={control}
                        id="mandatoryForSave-value"
                        label="Value"
                        name="mandatoryForSave.value"
                        data-testid="mandatoryForSave-value"
                        sxCustom={{ flex: 1, minWidth: 0 }}
                    />
                </Stack>
            </Stack>
        </Card>
    );
}
